import { useState, useEffect } from 'react'
import axios from 'axios'
import { getAuthToken } from '../../modules/auth/helpers'
import configs from '../../configs/configs'
import { RezResponse } from '../data/RezResponse'
import { ConfigResponse } from '../data/ConfigResponse'
import { StatusCodes } from '../data/Enums'
import { AvailableVehicle } from '../data/AvailableVehicle'
import { RegionResponse } from '../data/RegionResponse'
import { AddOnResponse } from '../data/AddOnResponse'
import { LocationExtended } from '../data/LocationExtended'

export const useGenerateAvailability = (requestBody: any, refresh: boolean) => {
  const initVehicle: RezResponse<AvailableVehicle> = {
    statusCode: StatusCodes.InIt,
    data: {
      results: 0,
      availableItems: []
    },
    errorMessage: ''
  }
  const [vehicles, setVehicles] = useState(initVehicle)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const token = getAuthToken()
  const url = configs.REZ_URL
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        if (token) {
          const response = await axios.post(
            `${url}/api/Reservation/Partner/GenerateAvailability?dealerNumber=${requestBody.dealerNumber}&oemName=${requestBody.oemName}`,
            requestBody,
            {
              headers: {
                Authorization: 'Bearer ' + token
              },
              withCredentials: false
            }
          )
          setError('')
          setVehicles(response.data)
        }
        setIsLoading(false)
      } catch (err: any) {
        setError(err.message)
        setIsLoading(false)
      }
    }
    if (refresh) {
      fetchData()
    }
  }, [token])

  return { vehicles, isLoading, error }
}

export const useGetConfig = (oemName: string | undefined, dealerNumber: string | undefined, refresh: boolean) => {
  const initVehicle: RezResponse<ConfigResponse[]> = {
    statusCode: StatusCodes.InIt,
    data: [
      {
        category: '',
        name: '',
        value: []
      }
    ],
    errorMessage: ''
  }
  const [configData, setConfigData] = useState(initVehicle)
  const [isLoadingConfigData, setIsLoadingConfigData] = useState(false)
  const [errorConfigData, setErrorConfigData] = useState('')

  const token = getAuthToken()
  const url = configs.REZ_URL
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingConfigData(true)
      try {
        if (token && oemName && dealerNumber) {
          const response = await axios.get(`${url}/api/Config?dealerNumber=${dealerNumber}&oemName=${oemName}`, {
            headers: {
              Authorization: 'Bearer ' + token
            },
            withCredentials: false
          })
          setErrorConfigData('')
          setConfigData(response.data)
        }
        setIsLoadingConfigData(false)
      } catch (err: any) {
        setErrorConfigData(err.message)
        setIsLoadingConfigData(false)
      }
    }
    if (refresh) {
      fetchData()
    }
  }, [token])

  return { isLoadingConfigData, errorConfigData, configData }
}

export const useGetRegions = (refresh: boolean) => {
  const initVehicle: RezResponse<RegionResponse[]> = {
    statusCode: StatusCodes.InIt,
    data: [],
    errorMessage: ''
  }
  const [regions, setRegions] = useState(initVehicle)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const token = getAuthToken()
  const url = configs.REZ_URL
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        if (token) {
          const response = await axios.get(`${url}/api/Location/Partner/GetRegions?countryCode=US`, {
            headers: {
              Authorization: 'Bearer ' + token
            },
            withCredentials: false
          })
          setError('')
          setRegions(response.data)
        }
        setIsLoading(false)
      } catch (err: any) {
        setError(err.message)
        setIsLoading(false)
      }
    }
    if (refresh) {
      fetchData()
    }
  }, [token])

  return { regions, isLoading, error }
}

export const useGetLocationData = (oemName: string | undefined, dealerNumber: string | undefined, refresh: boolean) => {
  const initLocationData: RezResponse<LocationExtended> = {
    statusCode: StatusCodes.InIt,
    data: {
      locationId: undefined,
      locationAddress: undefined,
      locationName: '',
      timeZoneId: ''
    },
    errorMessage: ''
  }
  const [locationData, setLocationData] = useState(initLocationData)
  const [isLoadingLocationData, setIsLoadingLocationData] = useState(false)
  const [errorLocationData, setErrorLocationData] = useState('')

  const token = getAuthToken()
  const url = configs.REZ_URL

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingLocationData(true)
      try {
        if (token && oemName && dealerNumber) {
          const response = await axios.get(`${url}/api/Location/partner?dealerNumber=${dealerNumber}&oemName=${oemName}`, {
            headers: {
              Authorization: 'Bearer ' + token
            },
            withCredentials: false
          })
          setErrorLocationData('')
          setLocationData(response.data)
        }
        setIsLoadingLocationData(false)
      } catch (err: any) {
        setErrorLocationData(err.message)
        setIsLoadingLocationData(false)
      }
    }
    if (refresh) {
      fetchData()
    }
  }, [token])

  return { isLoadingLocationData, errorLocationData, locationData }
}

export const useGetAddOns = (oemName: string | undefined, dealerNumber: string | undefined, refresh: boolean) => {
  const initResponse: RezResponse<AddOnResponse[]> = {
    statusCode: StatusCodes.InIt,
    data: [],
    errorMessage: ''
  }
  const [addOns, setAddOns] = useState(initResponse)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const token = getAuthToken()

  const url = configs.REZ_URL
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        if (token && oemName && dealerNumber) {
          const response = await axios.get(`${url}/api/AdditionalFee/Partner/GetAdditionalFees?dealerNumber=${dealerNumber}&oemName=${oemName}`, {
            headers: {
              Authorization: 'Bearer ' + token
            },
            withCredentials: false
          })
          setError('')
          setAddOns(response.data)
        }
        setIsLoading(false)
      } catch (err: any) {
        setError(err.message)
        setIsLoading(false)
      }
    }
    if (refresh) {
      fetchData()
    }
  }, [token, oemName, dealerNumber])
  return { addOns, isLoading, error }
}
